import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  Label,
  Select,
} from '@/components/ui';
import {
  useAutomation,
  useAutomationActions,
  type DealSnapshot,
  type SandboxResolvedAction,
} from '@/lib/automations-store';
import { resolveCatalogLabel } from '@/lib/catalogs-store';
import { useClientNome } from '@/lib/clients-store';
import type { Deal } from '@/lib/comercial-types';
import { getStageInfo } from '@/lib/comercial-types';

/**
 * Modal de sandbox — preview de tokens da automação contra um card real.
 * Backend resolve (decisão 3 do Cowork — single source of truth com dispatch).
 *
 * Layout 2 colunas:
 *   - Esquerda: definição (trigger + actions cruas)
 *   - Direita: preview (cada action com tokens resolvidos pelo backend)
 *
 * NÃO executa actions de verdade (backend rota `/sandbox` não tem side
 * effects — só substitui tokens).
 */

export interface AutomationSandboxDialogProps {
  open: boolean;
  onClose: () => void;
  automationId: string;
  deals: Deal[];
}

/**
 * Constrói o snapshot pro backend. `clienteNome` vem do consumer (via
 * `useClientNome(deal.clienteId)` no componente) — backend resolve tokens
 * `{{cliente}}` usando o nome legível, NÃO o ID. BUG-V2-001 (2026-05-18).
 */
function dealToSnapshot(deal: Deal, clienteNome: string): DealSnapshot {
  return {
    id: deal.id,
    cliente: clienteNome || deal.clienteId,
    produto: resolveCatalogLabel('comercial', 'produtos', deal.produto),
    servico: resolveCatalogLabel('comercial', 'servicos', deal.servico),
    valor: deal.valor,
    responsavelId: deal.responsavelId,
    responsavelNome: deal.responsavelNome,
    colunaAtualLabel: getStageInfo(deal.fase).label,
  };
}

export function AutomationSandboxDialog({
  open,
  onClose,
  automationId,
  deals,
}: AutomationSandboxDialogProps) {
  const automation = useAutomation(automationId);
  const { sandbox } = useAutomationActions(automation?.workflowSlug ?? 'comercial');
  const [selectedDealId, setSelectedDealId] = useState<string>(deals[0]?.id ?? '');
  const [preview, setPreview] = useState<SandboxResolvedAction[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedDeal = deals.find((d) => d.id === selectedDealId);
  // BUG-V2-001 — resolve nome do cliente selecionado pra mandar como
  // `cliente` legível no snapshot (token {{cliente}} renderiza nome, não UUID).
  const selectedClienteNome = useClientNome(selectedDeal?.clienteId);

  async function handleSimulate() {
    if (!selectedDeal || !automation) return;
    setLoading(true);
    setError(null);
    try {
      const snapshot = dealToSnapshot(selectedDeal, selectedClienteNome);
      const result = await sandbox(automation.id, snapshot);
      setPreview(result.actions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao simular');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>
          Testar automação{automation ? ` · ${automation.nome}` : ''}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          <div>
            <Label htmlFor="sandbox-card">Testar com qual card?</Label>
            <Select
              id="sandbox-card"
              data-testid="sandbox-card-select"
              value={selectedDealId}
              onChange={(e) => {
                setSelectedDealId(e.target.value);
                setPreview(null);
              }}
            >
              {deals.length === 0 ? (
                <option value="">Sem cards disponíveis</option>
              ) : (
                /* BUG-V2-001: label do option usa `titulo` (legível) +
                   prefixo curto do clienteId. Pra mostrar nome resolvido em
                   cada option teríamos que chamar hook em loop (proibido).
                   Caller do sandbox geralmente sabe qual card é via titulo. */
                deals.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.titulo}
                  </option>
                ))
              )}
            </Select>
          </div>

          <div>
            <Button
              type="button"
              size="sm"
              data-testid="sandbox-simulate"
              onClick={handleSimulate}
              loading={loading}
              disabled={!selectedDeal || !automation}
            >
              Simular trigger
            </Button>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-soft border-l-2 border-red text-red-deep text-sm">
              {error}
            </div>
          )}

          {automation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              {/* Esquerda: definição */}
              <div className="rounded-xl border border-line dark:border-line-dark p-3">
                <p className="text-xs font-semibold text-ink-3 dark:text-ink-dark-3 uppercase tracking-wider mb-2">
                  Definição
                </p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-ink-3">Trigger:</span>{' '}
                    <code className="text-xs">{automation.trigger.type}</code>{' '}
                    @ <strong>{automation.trigger.config.columnSlug}</strong>
                    {automation.trigger.config.days != null && (
                      <span> ({automation.trigger.config.days}d)</span>
                    )}
                  </div>
                  <ul className="space-y-1 text-xs text-ink-2 dark:text-ink-dark-2">
                    {automation.actions.map((a, i) => (
                      <li key={i} className="border-l-2 border-line pl-2">
                        <strong>{a.type}</strong>
                        {'target' in a && <> → {a.target}</>}
                        {a.type === 'move_card' && <> → coluna <code>{a.targetColumnSlug}</code></>}
                        {a.type === 'assign_responsavel' && <> → <code>{a.strategy}</code></>}
                        {'message' in a && (
                          <p className="italic mt-0.5 whitespace-pre-wrap">
                            {a.message}
                          </p>
                        )}
                        {'subject' in a && (
                          <p className="italic mt-0.5">{a.subject}</p>
                        )}
                        {a.type === 'create_task' && (
                          <p className="italic mt-0.5">
                            {a.texto} (prazo D+{a.prazoDiasUteis}d úteis)
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Direita: preview */}
              <div
                className="rounded-xl border border-line dark:border-line-dark p-3"
                data-testid="sandbox-preview"
              >
                <p className="text-xs font-semibold text-ink-3 dark:text-ink-dark-3 uppercase tracking-wider mb-2">
                  Preview (tokens resolvidos)
                </p>
                {preview === null ? (
                  <p className="text-xs text-ink-3 italic">
                    Clique &ldquo;Simular trigger&rdquo; pra ver o resultado.
                  </p>
                ) : preview.length === 0 ? (
                  <p className="text-xs text-ink-3 italic">Sem ações.</p>
                ) : (
                  <ul className="space-y-2">
                    {preview.map((p, i) => (
                      <li
                        key={i}
                        data-testid={`sandbox-preview-${i}`}
                        className="text-xs border-l-2 border-red pl-2 space-y-0.5"
                      >
                        <p className="font-medium">
                          {p.type}
                          {p.target ? <> → {p.target}</> : null}
                        </p>
                        {p.description && (
                          <p className="text-ink-2 italic">{p.description}</p>
                        )}
                        {p.resolvedMessage && (
                          <p className="whitespace-pre-wrap text-ink-2">
                            <strong>
                              {p.type === 'create_task' ? 'Texto da tarefa:' : 'Mensagem:'}
                            </strong>{' '}
                            {p.resolvedMessage}
                          </p>
                        )}
                        {p.resolvedSubject && (
                          <p className="text-ink-2">
                            <strong>Assunto:</strong> {p.resolvedSubject}
                          </p>
                        )}
                        {p.resolvedBody && (
                          <p className="whitespace-pre-wrap text-ink-2">
                            <strong>Corpo:</strong> {p.resolvedBody}
                          </p>
                        )}
                        {/* targetsResolved só faz sentido pras actions que
                            têm um conceito de "destinatário"; move_card e
                            assign_responsavel não têm — UI omite. */}
                        {p.type !== 'move_card' &&
                          p.type !== 'assign_responsavel' && (
                            <p className="text-ink-3">
                              Destinatários resolvidos:{' '}
                              {p.targetsResolved.length || 'nenhum'}
                              {p.targetsResolved.length > 0 && (
                                <code className="ml-1">
                                  [{p.targetsResolved.join(', ')}]
                                </code>
                              )}
                            </p>
                          )}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-ink-3 italic mt-3">
                  Preview apenas — nenhuma ação foi executada.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
